import type {
  KYCQuestionAnswer,
  KYCQuestionDefinition,
  KYCQuestionOption,
  KYCQuestionnaireElement,
} from '@anchorage/graphql/generated';
import {
  KYCQuestionType,
  KYCQuestionnaireElementLayoutType,
  KYCQuestionnaireElementType,
} from '@anchorage/graphql/generated';
import { format } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';

import type { FormAnswer, ShouldShowElement } from './types';

// A leading "!" marks a negated trigger atom; strips it to get the underlying option id.
const stripNegation = (optionId: string): string =>
  optionId.startsWith('!') ? optionId.slice(1) : optionId;

export const showElementBasedOnAnsweredOptions = ({
  triggerOptionID,
  answeredOptions,
  questions,
  groupNameAndIndex,
}: ShouldShowElement): boolean => {
  if (!triggerOptionID || !triggerOptionID.length) {
    return true;
  }

  const isOptionAnswered = (optionId: string): boolean => {
    const answeredQuestionId =
      answeredOptions?.[
        groupNameAndIndex ? `${groupNameAndIndex}.${optionId}` : optionId
      ];

    if (!answeredQuestionId) {
      return false;
    }

    // multiple level trigger option
    const previousQuestion = questions?.find(
      ({ id }) => answeredQuestionId === id,
    );

    if (!previousQuestion?.triggerOptionID) {
      return true;
    }

    return showElementBasedOnAnsweredOptions({
      triggerOptionID: previousQuestion?.triggerOptionID,
      answeredOptions,
      questions,
    });
  };

  //{{"o1", "o2"}, {"o3"}} means [[o1 AND o2] OR [o3]],
  //and a leading "!" means the option is not answered, e.g. {"!o1"} means [NOT o1]
  return triggerOptionID.some((options) =>
    options?.every((optionId: string) =>
      optionId.startsWith('!')
        ? !isOptionAnswered(stripNegation(optionId))
        : isOptionAnswered(optionId),
    ),
  );
};

export const convertQuestionnaireAnswers = (
  answers: KYCQuestionAnswer[],
  allQuestions?: KYCQuestionDefinition[],
): Record<string, Partial<FormAnswer> | undefined> =>
  answers.reduce(
    (
      acc,
      { questionID, freeformText, group, options },
    ): Record<string, Partial<FormAnswer> | undefined> => {
      const group_answers = group?.map((rowGroup) => {
        return rowGroup.reduce(
          (
            acc,
            { questionID: questionInGroupID, freeformText = '', options = [] },
          ) => {
            const questionsInGroup = allQuestions?.find(
              ({ id }) => id === questionID,
            )?.group;
            const questionInGroup = questionsInGroup?.find(
              ({ id }) => id === questionInGroupID,
            );
            const isMultiOption =
              questionInGroup?.type === KYCQuestionType.DROPDOWN_MULTI_SELECT ||
              questionInGroup?.type === KYCQuestionType.CHECKBOX;

            return {
              ...acc,
              [questionInGroupID]: {
                freeformText:
                  questionInGroup?.type === KYCQuestionType.DATE && freeformText
                    ? new Date(freeformText)
                    : (freeformText ?? ''),
                options: isMultiOption ? [options ?? []].flat() : options?.[0],
                file:
                  questionInGroup?.type === KYCQuestionType.UPLOAD &&
                  freeformText
                    ? [freeformText]
                    : undefined,
              },
            };
          },
          {},
        );
      });

      const question = allQuestions?.find(({ id }) => id === questionID);
      const isMultiOption =
        question?.type === KYCQuestionType.DROPDOWN_MULTI_SELECT ||
        question?.type === KYCQuestionType.CHECKBOX;
      // @ts-expect-error - TS 5.9.3 requires string but Date constructor is intentional here. TODO: investigate proper type for form answer
      return {
        ...acc,
        [questionID]: {
          freeformText:
            question?.type === KYCQuestionType.DATE && freeformText
              ? new Date(freeformText)
              : (freeformText ?? ''),
          options: isMultiOption ? [options ?? []].flat() : options?.[0],
          group_answers: group_answers || undefined,
          file:
            question?.type === KYCQuestionType.UPLOAD && freeformText
              ? [freeformText]
              : undefined,
        },
      };
    },
    {},
  );

export const convertAnswersToSubmit = ({
  answers,
}: {
  answers: Record<string, FormAnswer | undefined>;
}) => {
  const finalAnswers: KYCQuestionAnswer[] = [];

  const answersKeys = Object.keys(answers);

  answersKeys.forEach((answerKey) => {
    const answerData = answers[answerKey];

    if (answerData === undefined || answerData === null) {
      return;
    }

    const finalData: Partial<KYCQuestionAnswer> = {
      questionID: answerKey,
      options: [] as string[],
      freeformText: '',
      group: [],
    };

    // answer is DATE
    if ((answerData.freeformText as unknown) instanceof Date) {
      const isValidDate = isValid(answerData.freeformText);
      finalData.freeformText = isValidDate
        ? format(answerData.freeformText as unknown as Date, 'MM/dd/yyyy')
        : answerData.freeformText;
      // answer is TEXTFIELD or TEXT_AREA
    } else if (answerData.freeformText) {
      finalData.freeformText = answerData.freeformText;
    }

    // answer is CHECKBOX or RADIO or DROPDOWN or DROPDOWN_MULTI_SELECT or RADIO_GROUP
    if (
      (Array.isArray(answerData.options) && !!answerData.options.length) ||
      (!Array.isArray(answerData.options) && answerData.options)
    ) {
      const options = Array.isArray(answerData.options)
        ? answerData.options
        : [answerData.options];
      finalData.options = options;
    }

    // answer is GROUP
    if (answerData.group_answers?.length) {
      finalData.group = answerData.group_answers.map((group) => {
        const groupAnswers: Record<string, FormAnswer | undefined> = {};
        Object.keys(group).forEach((questionID) => {
          // do not add useFieldArray id
          if (questionID === 'id') {
            return;
          }
          groupAnswers[questionID] = group[questionID];
        });

        return convertAnswersToSubmit({ answers: groupAnswers });
      });
    }

    finalAnswers.push(finalData as KYCQuestionAnswer);
  });

  return finalAnswers;
};

export const getElementQuestions = (
  element: KYCQuestionnaireElement,
): KYCQuestionDefinition[] => {
  if (
    element.type === KYCQuestionnaireElementType.QUESTION &&
    element.question
  ) {
    return [element.question];
  }

  const subElementsQuestions =
    element.elements?.reduce((accumulator: KYCQuestionDefinition[], item) => {
      return [...accumulator, ...getElementQuestions(item)];
    }, [] as KYCQuestionDefinition[]) || [];

  return subElementsQuestions;
};

/**
 * Lists the top-level questions that the form does not show: a trigger in
 * their ancestor chain (main nav, sub nav, block) or their own trigger is not
 * answered. The yup schema skips these questions (see
 * createQuestionnaireResolver).
 */
export const getHiddenQuestionIDs = ({
  body,
  answeredOptions,
  allQuestions = getElementQuestions(body),
}: {
  body: KYCQuestionnaireElement;
  answeredOptions: Record<string, string>;
  allQuestions?: KYCQuestionDefinition[];
}): string[] => {
  const hiddenQuestionIDs: string[] = [];

  const isShown = (triggerOptionID: ShouldShowElement['triggerOptionID']) =>
    showElementBasedOnAnsweredOptions({
      triggerOptionID,
      answeredOptions,
      questions: allQuestions,
    });

  const collect = (element: KYCQuestionnaireElement, parentShown: boolean) => {
    if (element.type === KYCQuestionnaireElementType.QUESTION) {
      if (
        element.question &&
        !(parentShown && isShown(element.question.triggerOptionID))
      ) {
        hiddenQuestionIDs.push(element.question.id);
      }
      return;
    }

    const shown = parentShown && isShown(element.triggeredBy);
    for (const child of element.elements ?? []) {
      collect(child, shown);
    }
  };

  collect(body, true);

  return hiddenQuestionIDs;
};

/**
 * Lists the form paths of the triggered questions inside the rows of a GROUP
 * question whose trigger is not answered in that row, for example
 * `owners.group_answers.1.controlTitle`. Groups that are hidden as a whole are
 * skipped: their id is already in `hiddenQuestionIDs`. The group schema skips
 * these questions (see createGroupQuestionSchema).
 */
export const getHiddenGroupQuestionPaths = ({
  answers,
  answeredOptions = questionIDsByAnsweredOptions(answers),
  allQuestions,
  hiddenQuestionIDs,
}: {
  answers: Record<string, FormAnswer | undefined>;
  answeredOptions?: Record<string, string>;
  allQuestions: KYCQuestionDefinition[];
  hiddenQuestionIDs: string[];
}): string[] => {
  const hiddenPaths: string[] = [];

  for (const question of allQuestions) {
    if (
      question.type !== KYCQuestionType.GROUP ||
      hiddenQuestionIDs.includes(question.id)
    ) {
      continue;
    }

    const triggeredQuestions = (question.group ?? []).filter(
      ({ triggerOptionID }) => triggerOptionID?.length,
    );
    const rows = answers[question.id]?.group_answers ?? [];

    rows.forEach((_, index) => {
      const groupNameAndIndex = `${question.id}.group_answers.${index}`;

      for (const triggeredQuestion of triggeredQuestions) {
        const isShown = showElementBasedOnAnsweredOptions({
          triggerOptionID: triggeredQuestion.triggerOptionID,
          answeredOptions,
          questions: allQuestions,
          groupNameAndIndex,
        });

        if (!isShown) {
          hiddenPaths.push(`${groupNameAndIndex}.${triggeredQuestion.id}`);
        }
      }
    });
  }

  return hiddenPaths;
};

export const getNavElements = (elements: KYCQuestionnaireElement[]) =>
  elements?.filter(({ type }) => type === KYCQuestionnaireElementType.NAV);

export const getAllNavigableNavElements = (
  elements: KYCQuestionnaireElement[],
) => {
  const navElements = getNavElements(elements);
  const navIds: KYCQuestionnaireElement[] = [];
  navElements?.forEach((navElement) => {
    const subNavElements = getNavElements(navElement.elements);
    subNavElements?.length
      ? navIds.push(...getAllNavigableNavElements(subNavElements))
      : navIds.push(navElement);
  });

  return navIds;
};

export const getQuestionNavElementID = (
  pages: KYCQuestionnaireElement[],
  questionID: string,
) => {
  const navElement = pages?.find((nav) => {
    const questions = getElementQuestions(nav);
    return questions.some(({ id }) => id === questionID);
  });

  return navElement?.id;
};

export const getQuestion = (
  pages: KYCQuestionnaireElement[],
  questionID: string,
) => {
  let question: KYCQuestionDefinition | undefined;
  for (const page of pages) {
    const questions = getElementQuestions(page);
    const foundQuestion = questions.find(({ id }) => id === questionID);
    if (foundQuestion) {
      question = foundQuestion;
      break;
    }
  }

  return question;
};

// It assumes type QUESTION is always under a type BLOCK
export const getClosestSectionTitle = (
  elements: KYCQuestionnaireElement[],
  questionID: string,
): string | undefined => {
  // Get the root element that contains the question
  const elementId = getQuestionNavElementID(elements, questionID);
  const element = elements.find((element) => element.id === elementId);

  if (!element) {
    return undefined;
  }

  // store the title of the element that contains the question
  const sectionTitle = element.title;

  // go through the sub elements and check if any is already the question itself
  for (const subElement of element?.elements || []) {
    // if yes, return the title of the parent element because we found the closest section
    if (
      subElement.type === KYCQuestionnaireElementType.QUESTION &&
      subElement.question?.id === questionID
    ) {
      return sectionTitle;
      // if not but there are more sub elements, go deeper and get the title of the sub elements
    } else if (subElement.elements?.length) {
      return (
        getClosestSectionTitle(subElement.elements || [], questionID) ||
        sectionTitle
      );
    }
  }

  return undefined;
};

export const questionHasAnswer = (
  questionId: string,
  answers: Record<string, FormAnswer | undefined>,
): boolean => {
  const answer = answers[questionId];
  if (!answer) {
    return false;
  }

  if (answer.freeformText || answer.options?.length) {
    return true;
  }

  if (answer.group_answers?.length) {
    return answer.group_answers.some((group) =>
      Object.keys(group).some((key) => questionHasAnswer(key, group)),
    );
  }

  return false;
};

// get the related question equivalent option ids of the answered question
// this is done by getting the tag of the answered option and finding the option with the same tag in the related question
export const relatedQuestionAnsweredOptionIds = (
  answeredOptionIds: string[] | string,
  currentQuestionPossibleOptions: KYCQuestionOption[],
  relatedQuestionPossibleOptions: KYCQuestionOption[],
) => {
  if (!Array.isArray(answeredOptionIds)) {
    const answeredTag = getOptionTagById(
      currentQuestionPossibleOptions,
      answeredOptionIds,
    );
    return getOptionIdByTag(relatedQuestionPossibleOptions, answeredTag);
  } else {
    return answeredOptionIds
      .map((optionId) => {
        const answeredTag = getOptionTagById(
          currentQuestionPossibleOptions,
          optionId,
        );
        return getOptionIdByTag(relatedQuestionPossibleOptions, answeredTag);
      })
      .filter((option) => !!option);
  }
};

export const getOptionIdByTag = (
  options: KYCQuestionOption[],
  tag: string,
): string => options.find((option) => option.tag === tag)?.id || '';

export const getOptionTagById = (
  options: KYCQuestionOption[],
  id: string,
): string => options.find((option) => option.id === id)?.tag || '';

export const questionIDsByAnsweredOptions = (
  answers: Record<string, FormAnswer | undefined>,
): Record<string, string> => {
  let questionIDByAnsweredOptions: Record<string, string> = {};
  for (const [questionID, answer] of Object.entries(answers)) {
    if (!answer) {
      continue;
    }
    // if option is string
    if (typeof answer.options === 'string') {
      questionIDByAnsweredOptions[answer.options] = questionID;
    }
    // if options is array
    if (Array.isArray(answer.options)) {
      if (answer?.options.length) {
        for (const optionID of answer.options) {
          questionIDByAnsweredOptions[optionID] = questionID;
        }
      }
    }

    if (answer?.group_answers?.length) {
      answer.group_answers?.forEach((groupAnswer, index) => {
        const groupQuestionIDsByAnsweredOptions =
          questionIDsByAnsweredOptions(groupAnswer);

        // for each pair optionID-questionID, we add the group name and index to the optionID and questionID
        // so we can know which group and index the answered option is from
        Object.keys(groupQuestionIDsByAnsweredOptions).forEach((optionID) => {
          const groupNameAndIndex = `${questionID}.group_answers.${index}`;
          questionIDByAnsweredOptions = {
            ...questionIDByAnsweredOptions,
            [`${groupNameAndIndex}.${optionID}`]: `${groupNameAndIndex}.${groupQuestionIDsByAnsweredOptions[optionID]}`,
          };
        });
      });
    }
  }

  return questionIDByAnsweredOptions;
};

// Same rule as the BE: at least one option, or text that is not blank. An
// upload counts once its digest is in `freeformText`.
const answerHasContent = ({
  options,
  freeformText,
}: Pick<FormAnswer, 'options' | 'freeformText'>): boolean =>
  (Array.isArray(options) ? options.length > 0 : !!options) ||
  String(freeformText ?? '').trim() !== '';

/**
 * Adds the question atoms to `answeredOptions`. A trigger atom
 * `<questionID>_notempty` holds when that question is shown and its answer has
 * content; a GROUP question needs a row with a child that is shown in that row
 * and answered. Inside a row the atom gets the row prefix, like the options.
 *
 * Walks the body in document order, as the BE does, because whether a
 * question is shown can depend on the atoms of earlier questions. A question
 * has no atom when an option id already takes the name.
 */
export const addQuestionAtoms = ({
  body,
  answers,
  answeredOptions,
  allQuestions = getElementQuestions(body),
}: {
  body: KYCQuestionnaireElement;
  answers: Record<string, FormAnswer | undefined>;
  answeredOptions: Record<string, string>;
  allQuestions?: KYCQuestionDefinition[];
}): Record<string, string> => {
  const withAtoms = { ...answeredOptions };
  const optionIDs = new Set(
    flattenQuestionDefinitions(allQuestions).flatMap(({ options }) =>
      (options ?? []).map(({ id }) => id),
    ),
  );

  const atomOf = (questionID: string) => {
    const atom = `${questionID}_notempty`;
    return optionIDs.has(atom) ? undefined : atom;
  };

  const isShown = (
    triggerOptionID: ShouldShowElement['triggerOptionID'],
    groupNameAndIndex?: string,
  ) =>
    showElementBasedOnAnsweredOptions({
      triggerOptionID,
      answeredOptions: withAtoms,
      questions: allQuestions,
      groupNameAndIndex,
    });

  const addGroupRowAtoms = (question: KYCQuestionDefinition): boolean => {
    let hasAnsweredRow = false;
    answers[question.id]?.group_answers?.forEach((row, index) => {
      const groupNameAndIndex = `${question.id}.group_answers.${index}`;
      for (const child of question.group ?? []) {
        const atom = atomOf(child.id);
        const answer = row[child.id];
        if (
          atom &&
          answer &&
          answerHasContent(answer) &&
          isShown(child.triggerOptionID, groupNameAndIndex)
        ) {
          withAtoms[`${groupNameAndIndex}.${atom}`] =
            `${groupNameAndIndex}.${child.id}`;
          hasAnsweredRow = true;
        }
      }
    });
    return hasAnsweredRow;
  };

  const visit = (element: KYCQuestionnaireElement, parentShown: boolean) => {
    const shown = parentShown && isShown(element.triggeredBy);
    const question =
      element.type === KYCQuestionnaireElementType.QUESTION
        ? element.question
        : undefined;

    if (question && shown && isShown(question.triggerOptionID)) {
      const answer = answers[question.id];
      const isAnswered =
        question.type === KYCQuestionType.GROUP
          ? addGroupRowAtoms(question)
          : !!answer && answerHasContent(answer);
      const atom = atomOf(question.id);
      if (atom && isAnswered) {
        withAtoms[atom] = question.id;
      }
    }

    for (const child of element.elements ?? []) {
      visit(child, shown);
    }
  };

  visit(body, true);

  return withAtoms;
};

export const questionHasValue = (
  question: KYCQuestionDefinition,
  answer: FormAnswer,
): boolean => {
  if (question.type === KYCQuestionType.UPLOAD) {
    return Array.isArray(answer.file)
      ? answer.file.length > 0
      : answer.file !== undefined;
  }

  if (
    question.type === KYCQuestionType.CHECKBOX ||
    question.type === KYCQuestionType.DROPDOWN_MULTI_SELECT ||
    question.type === KYCQuestionType.DROPDOWN ||
    question.type === KYCQuestionType.RADIO ||
    question.type === KYCQuestionType.RADIO_GROUP ||
    question.type === KYCQuestionType.CARD_SELECT
  ) {
    return Array.isArray(answer.options)
      ? answer.options.length > 0
      : answer.options !== undefined;
  }

  return !!answer.freeformText;
};

export const getQuestionPostfixFromType = (type: KYCQuestionType): string => {
  switch (type) {
    case KYCQuestionType.CHECKBOX:
    case KYCQuestionType.DROPDOWN_MULTI_SELECT:
    case KYCQuestionType.DROPDOWN:
    case KYCQuestionType.RADIO:
    case KYCQuestionType.RADIO_GROUP:
    case KYCQuestionType.CARD_SELECT:
      return 'options';
    case KYCQuestionType.UPLOAD:
      return 'file';
    default:
      return 'freeformText';
  }
};

const flattenQuestionDefinitions = (
  questions: KYCQuestionDefinition[],
): KYCQuestionDefinition[] =>
  questions.flatMap((question) => [
    question,
    ...flattenQuestionDefinitions(question.group ?? []),
    ...flattenQuestionDefinitions(
      (question.options ?? []).flatMap((option) => option.questionGroup ?? []),
    ),
  ]);

// Triggers reference option IDs, not question IDs, and a negated atom carries
// a leading "!". Resolves each trigger option to the question whose own options
// contain it (including GROUP sub-questions and RADIO_GROUP nested questions),
// deduped by question ID.
export const getTriggeringQuestions = (
  triggerOptionID: string[][] | null | undefined,
  allQuestions: KYCQuestionDefinition[] | undefined,
): KYCQuestionDefinition[] => {
  if (!triggerOptionID?.length || !allQuestions?.length) {
    return [];
  }

  const flattened = flattenQuestionDefinitions(allQuestions);

  const optionIdToQuestion = new Map<string, KYCQuestionDefinition>();
  for (const question of flattened) {
    for (const option of question.options ?? []) {
      if (!optionIdToQuestion.has(option.id)) {
        optionIdToQuestion.set(option.id, question);
      }
    }
  }

  const triggeringQuestions: KYCQuestionDefinition[] = [];
  const seenQuestionIds = new Set<string>();

  for (const optionId of new Set(triggerOptionID.flat())) {
    const owner = optionIdToQuestion.get(stripNegation(optionId));
    if (owner && !seenQuestionIds.has(owner.id)) {
      seenQuestionIds.add(owner.id);
      triggeringQuestions.push(owner);
    }
  }

  return triggeringQuestions;
};

// MESSAGE marks a reviewer-authored custom message block; its subtitle must
// keep the strong text color, unlike regular section subtitles (ONMON-5326).
export const getBlockSubtitleColor = (
  layout: KYCQuestionnaireElement['layout'],
): 'strong' | 'muted' =>
  layout?.type === KYCQuestionnaireElementLayoutType.MESSAGE
    ? 'strong'
    : 'muted';
